import { Injectable } from '@angular/core';

/**
 * Service for providing colored marker icons.
 * Uses pre-generated high-resolution 4x PNG assets for crisp rendering.
 */
@Injectable({
  providedIn: 'root'
})
export class ColoredMarkerService {
  // Map hex colors to file name slugs
  private readonly COLOR_TO_NAME: { [hex: string]: string } = {
    '#2C36D6': 'figma-blue',
    '#0091FF': 'sky-blue',
    '#4BA591': 'teal',
    '#D86A99': 'pink',
    '#DBBA60': 'gold',
    '#3D57C5': 'deep-blue',
    '#00D9A8': 'mint',
    '#00C5D3': 'cyan',
    '#A08438': 'olive',
  };

  // Base path for colored markers
  private readonly COLORED_MARKERS_PATH = '/assets/icons/map/markers/colored';

  constructor() {}

  /**
   * Get a colored marker image URL for the specified marker type and color.
   * Returns the pre-generated PNG URL if color is in palette, otherwise falls back to default.
   *
   * @param markerType - 'bid_item' | 'activity' | 'delivery'
   * @param color - Hex color string (e.g., '#6583FF')
   * @returns string - URL to the marker PNG
   */
  getColoredMarkerUrl(
    markerType: 'bid_item' | 'activity' | 'delivery',
    color?: string
  ): string {
    if (!color) {
      return this.getDefaultMarkerUrl(markerType);
    }

    // Normalize color to uppercase for lookup
    const normalizedColor = color.toUpperCase();
    const colorName = this.COLOR_TO_NAME[normalizedColor];

    if (!colorName) {
      // Color not in palette, return default marker
      return this.getDefaultMarkerUrl(markerType);
    }

    // Return pre-generated colored marker
    return `${this.COLORED_MARKERS_PATH}/${markerType}_${colorName}@4x.png`;
  }

  /**
   * Async version for backwards compatibility.
   * Simply wraps the sync version in a resolved promise.
   */
  async getColoredMarkerUrlAsync(
    markerType: 'bid_item' | 'activity' | 'delivery',
    color?: string
  ): Promise<string> {
    return this.getColoredMarkerUrl(markerType, color);
  }

  /**
   * Check if a color is in the supported palette.
   */
  isColorSupported(color: string): boolean {
    return !!this.COLOR_TO_NAME[color.toUpperCase()];
  }

  /**
   * Get the default marker URL (static PNG) for fallback.
   */
  getDefaultMarkerUrl(markerType: 'bid_item' | 'activity' | 'delivery'): string {
    switch (markerType) {
      case 'bid_item':
        return '/assets/icons/map/markers/bid_items@4x.png';
      case 'activity':
        return '/assets/icons/map/markers/activities@4x.png';
      case 'delivery':
        return '/assets/map-markers/delivery-marker@4x.png';
      default:
        return '/assets/icons/map/markers/bid_items@4x.png';
    }
  }

  /**
   * Pre-generate markers - no longer needed with static assets.
   * Kept for backwards compatibility.
   */
  async preGenerateMarkers(colors: string[]): Promise<void> {
    // No-op - markers are pre-generated as static assets
  }

  /**
   * Clear cache - no longer needed with static assets.
   * Kept for backwards compatibility.
   */
  clearCache(): void {
    // No-op - using static assets
  }
}
